import { supabase } from "@/integrations/supabase/client"
import { NpoProfile } from "@/integrations/supabase/types/opportunities";

interface UpdateProfileData {
  id: string
  full_name: string
  email: string
  phone?: string
  isd_code?: string
}

export const updateProfile = async ({ id, full_name, email, phone, isd_code }: UpdateProfileData) => {
  const { data, error } = await supabase
    .from("profiles")
    .update({
      full_name,
      email,
      phone: phone ? `${isd_code}${phone}` : null,
      updated_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select()
    .single()

  if (error) {
    console.error("Error updating profile:", error)
    throw error
  }

  return data
}

export const upsertNPOProfile = async (userId: string, profileData: Partial<NpoProfile>) => {
  const validFields: (keyof NpoProfile)[] = [
    'org_name',
    'website',
    'logo_url',
    'description',
    'focus_areas',
    'sdg_alignment',
    'poc_first_name',
    'poc_last_name',
    'isd_code',
    'phone',
    'email',
    'address_line1',
    'address_line2',
    'state',
    'city',
    'country',
    'zip_code',
    'beneficiary_types',
    'slug'
  ];

  // Filter out any fields that aren't in the NpoProfile interface
  const filteredData = Object.keys(profileData).reduce((acc, key) => {
    if (validFields.includes(key as keyof NpoProfile)) {
      acc[key as keyof NpoProfile] = profileData[key as keyof NpoProfile];
    }
    return acc;
  }, {} as Partial<NpoProfile>);

  const { data, error } = await supabase
    .from('npo_profiles')
    .upsert({
      id: userId,
      ...filteredData,
      updated_at: new Date().toISOString()
    })
    .select()
    .single();

  if (error) {
    console.error("Error upserting NPO profile:", error);
    throw error;
  }

  return data;
};

export const getNPOCredits = async (npoId: string): Promise<number> => {
  const { data, error } = await supabase
    .from('user_credits')
    .select('balance')
    .eq('npo_id', npoId)
    .maybeSingle() as { data: { balance: number } | null, error: any };

  if (error) throw error;
  return data?.balance ?? 0;
}
